import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { forkJoin, map, Observable } from "rxjs";
import { APP_CONFIG } from "../../../core/config/app-config";
import {
    KnowledgeData,
    KnowledgeDocument,
    KnowledgeDraft,
    KnowledgeFaq,
    KnowledgeItem,
    KnowledgeSection,
    KnowledgeStatus,
    MalfunctionCode,
} from "../models/knowledge.models";

interface FaqResponse {
    clientId?: string;
    question?: string;
    answer?: string;
    status?: string;
    videoUrl?: string;
    videoDescription?: string;
    documentUrl?: string;
    documentDescription?: string;
    gmvid?: string;
    _id?: string;
}

interface DocumentResponse {
    clientId?: string;
    status?: string;
    fileUrl?: string;
    fileDescription?: string;
    objectId?: string;
    gmvid?: string;
    _id?: string;
}

interface MalfunctionResponse {
    clientId?: string;
    code?: string;
    malfunctionCodeIds?: string[];
    mfChain?: string[];
    faultDescription?: string;
    faultDetail?: string;
    status?: string;
    documentUrl?: string;
    documentDescription?: string;
    objectId?: string;
    gmvid?: string;
    _id?: string;
}

@Injectable({ providedIn: "root" })
export class KnowledgeApiService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(APP_CONFIG);
    private readonly url = `${this.config.apiBaseUrl}/servkb`;

    load(): Observable<KnowledgeData> {
        return forkJoin({
            faqs: this.http
                .get<FaqResponse[]>(`${this.url}/faq`)
                .pipe(map((items) => items.map((item) => this.mapFaq(item)))),
            documents: this.http
                .get<DocumentResponse[]>(`${this.url}/document`)
                .pipe(
                    map((items) => items.map((item) => this.mapDocument(item)))
                ),
            malfunctions: this.http
                .get<MalfunctionResponse[]>(`${this.url}/malfunctioncode`)
                .pipe(
                    map((items) =>
                        items.map((item) => this.mapMalfunction(item))
                    )
                ),
        });
    }

    save(
        section: KnowledgeSection,
        draft: KnowledgeDraft,
        current?: KnowledgeItem
    ): Observable<KnowledgeItem> {
        if (section === "faqs") return this.saveFaq(draft, current);
        if (section === "documents") return this.saveDocument(draft, current);
        return this.saveMalfunction(draft, current);
    }

    delete(item: KnowledgeItem): Observable<void> {
        const endpoint =
            item.kind === "faqs"
                ? "faq"
                : item.kind === "documents"
                  ? "document"
                  : "malfunctioncode";
        return this.http.delete<void>(
            `${this.url}/${endpoint}/${encodeURIComponent(item.id)}`
        );
    }

    setStatus(
        item: KnowledgeItem,
        status: KnowledgeStatus
    ): Observable<KnowledgeItem> {
        if (item.kind === "faqs") {
            return this.http
                .patch<FaqResponse>(`${this.url}/faq`, {
                    ...this.faqPayload(item),
                    status,
                })
                .pipe(
                    map((response) =>
                        this.mapFaq(response, { ...item, status })
                    )
                );
        }
        const endpoint =
            item.kind === "documents" ? "document" : "malfunctioncode";
        return this.http
            .patch<unknown>(`${this.url}/${endpoint}/${status}`, [item.id])
            .pipe(map(() => ({ ...item, status })));
    }

    private saveFaq(
        draft: KnowledgeDraft,
        current?: KnowledgeItem
    ): Observable<KnowledgeFaq> {
        const payload = {
            question: draft.question ?? "",
            answer: draft.answer ?? "",
            videoUrl: draft.videoUrl ?? "",
            videoDescription: draft.videoDescription ?? "",
            documentUrl: draft.documentUrl ?? "",
            documentDescription: draft.documentDescription ?? "",
        };
        if (current?.kind === "faqs") {
            const updated = { ...current, ...payload };
            return this.http
                .patch<FaqResponse>(`${this.url}/faq`, this.faqPayload(updated))
                .pipe(map((response) => this.mapFaq(response, updated)));
        }
        return this.http
            .post<FaqResponse[]>(`${this.url}/faq`, [payload])
            .pipe(map((items) => this.mapFaq(items[0] ?? {}, payload)));
    }

    private saveDocument(
        draft: KnowledgeDraft,
        current?: KnowledgeItem
    ): Observable<KnowledgeDocument> {
        const payload = {
            fileUrl: draft.fileUrl ?? "",
            fileDescription: draft.fileDescription ?? "",
        };
        if (current?.kind === "documents") {
            const updated = { ...current, ...payload };
            return this.http
                .patch<DocumentResponse>(`${this.url}/document`, {
                    ...payload,
                    clientId: updated.clientId,
                    status: updated.status,
                    gmvid: updated.id,
                    objectId: updated.id,
                    _id: updated.id,
                })
                .pipe(map((response) => this.mapDocument(response, updated)));
        }
        return this.http
            .post<DocumentResponse[]>(`${this.url}/document`, [payload])
            .pipe(map((items) => this.mapDocument(items[0] ?? {}, payload)));
    }

    private saveMalfunction(
        draft: KnowledgeDraft,
        current?: KnowledgeItem
    ): Observable<MalfunctionCode> {
        const payload = {
            code: draft.code ?? "",
            malfunctionCodeIds: draft.relatedCodeId
                ? [draft.relatedCodeId]
                : [],
            faultDescription: draft.faultDescription ?? "",
            faultDetail: draft.faultDetail ?? "",
            documentUrl: draft.documentUrl ?? "",
            documentDescription: draft.documentDescription ?? "",
        };
        if (current?.kind === "malfunctions") {
            const updated = { ...current, ...payload };
            return this.http
                .patch<MalfunctionResponse>(`${this.url}/malfunctioncode`, {
                    ...payload,
                    clientId: updated.clientId,
                    status: updated.status,
                    mfChain: updated.mfChain,
                    gmvid: updated.id,
                    objectId: updated.id,
                    _id: updated.id,
                })
                .pipe(
                    map((response) => this.mapMalfunction(response, updated))
                );
        }
        return this.http
            .post<MalfunctionResponse | MalfunctionResponse[]>(`${this.url}/malfunctioncode`, [payload])
            .pipe(map((response) => this.mapMalfunction(
                Array.isArray(response) ? response[0] ?? {} : response,
                payload,
            )));
    }

    private faqPayload(item: KnowledgeFaq) {
        return {
            clientId: item.clientId,
            status: item.status,
            question: item.question,
            answer: item.answer,
            videoUrl: item.videoUrl,
            videoDescription: item.videoDescription,
            documentUrl: item.documentUrl,
            documentDescription: item.documentDescription,
            gmvid: item.id,
            _id: item.id,
        };
    }

    private mapFaq(
        item: FaqResponse,
        fallback: Partial<KnowledgeFaq> = {}
    ): KnowledgeFaq {
        return {
            kind: "faqs",
            id: item.gmvid || item._id || fallback.id || "",
            clientId: item.clientId ?? fallback.clientId ?? "",
            status: this.status(item.status ?? fallback.status ?? ""),
            question: item.question ?? fallback.question ?? "",
            answer: item.answer ?? fallback.answer ?? "",
            videoUrl: item.videoUrl ?? fallback.videoUrl ?? "",
            videoDescription:
                item.videoDescription ?? fallback.videoDescription ?? "",
            documentUrl: item.documentUrl ?? fallback.documentUrl ?? "",
            documentDescription:
                item.documentDescription ?? fallback.documentDescription ?? "",
        };
    }

    private mapDocument(
        item: DocumentResponse,
        fallback: Partial<KnowledgeDocument> = {}
    ): KnowledgeDocument {
        return {
            kind: "documents",
            id: item.gmvid || item.objectId || item._id || fallback.id || "",
            clientId: item.clientId ?? fallback.clientId ?? "",
            status: this.status(item.status ?? fallback.status ?? ""),
            fileUrl: item.fileUrl ?? fallback.fileUrl ?? "",
            fileDescription:
                item.fileDescription ?? fallback.fileDescription ?? "",
        };
    }

    private mapMalfunction(
        item: MalfunctionResponse,
        fallback: Partial<MalfunctionCode> = {}
    ): MalfunctionCode {
        return {
            kind: "malfunctions",
            id: item.gmvid || item.objectId || item._id || fallback.id || "",
            clientId: item.clientId ?? fallback.clientId ?? "",
            status: this.status(item.status ?? fallback.status ?? ""),
            code: item.code ?? fallback.code ?? "",
            malfunctionCodeIds:
                item.malfunctionCodeIds ?? fallback.malfunctionCodeIds ?? [],
            mfChain: item.mfChain ?? fallback.mfChain ?? [],
            faultDescription:
                item.faultDescription ?? fallback.faultDescription ?? "",
            faultDetail: item.faultDetail ?? fallback.faultDetail ?? "",
            documentUrl: item.documentUrl ?? fallback.documentUrl ?? "",
            documentDescription:
                item.documentDescription ?? fallback.documentDescription ?? "",
        };
    }

    private status(status: string): KnowledgeStatus {
        return status === "pushed" ? "pushed" : "submitted";
    }
}
